import { Component, Input, ViewChild, OnInit, AfterViewInit, TemplateRef, Output, EventEmitter } from "@angular/core";
import { RowClickEvent, SortEvent } from "@fiduciaria/RequestCreation/Types/Interfaces/event-sort.interface";
import { DatatableComponent } from "@swimlane/ngx-datatable";
import { Column } from "@universal-ui";
import { BsModalService, BsModalRef } from "ngx-bootstrap/modal";
import { isObservable, Observable } from "rxjs";

import datatableMessages from "@assets/datatables/table-messages.json";

import { DatePipe } from "@shared/Pipes/date.pipe";
import { NumberPipe } from "@shared/Pipes/number.pipe";
import { CacheService } from "@shared/Services/cache.service";
import { ExcelService } from "@shared/Services/excel.service";
import { ObjectPropertiesService } from "@shared/Services/objectProperties.service";

import { UnitSharedService } from "@Unit/shared/services/unitshared.service";

import { FilterTableModalServer } from "./Modal/modalServer.component";

export interface PaginationResponse {
  currentPage: number;
  pageSize: number;
  maxPage: number;
}
@Component({
  selector: "filter-table-server",
  templateUrl: "./filterTableServer.component.html",
  styleUrls: ["./filterTableServer.component.scss"],
})
export class FilterTableServerComponent<T> implements OnInit, AfterViewInit {
  public offset: number = 0;
  public currentPage: number = 1;
  public maxPage: number = 0;
  public pageSize: number = 10;

  public allSelected: boolean = true;

  public tableId: string;

  @Input() public columnMode?: string;
  @Input() public filterValue: string = "";

  @Output() public serviceConsuming: EventEmitter<PaginationResponse> = new EventEmitter<PaginationResponse>();
  @Input() public serverMode?: Observable<T[]>;
  @Input() public serverSidePagination?: Observable<PaginationResponse>;
  @Input() public reset?: Observable<void>;

  @Input() public columns: Column[] = [];
  public displayColumns: Column[] = [];

  @Input() public searchCols: string[] = [];

  @Input() public data?: Observable<T[]> | T[];
  public displayData: T[] = [];
  public filteredData: T[] = [];
  public originalData: T[] = [];

  public itemCount: number = 0;
  @Input() public selectableRow: boolean = true;
  @Input() public selectedData: T[] = [];

  @Input() public excelName?: string;
  @Input() public customExport?: boolean;
  @Output() public excelExported: EventEmitter<T[]> = new EventEmitter<T[]>();

  @Input() public clickableRow: boolean = false;
  @Output() public rowClicked: EventEmitter<T> = new EventEmitter<T>();

  public filterTableModal?: BsModalRef;
  @ViewChild(DatatableComponent, { static: true }) private table?: DatatableComponent;
  public datatableMessages = { ...datatableMessages };
  public isUnitPortalStyle: boolean = false;
  @ViewChild("yesNoTemplate", { static: true }) public yesNoTemplate?: TemplateRef<boolean>;

  public shownPageNumber: number = 3;
  public pageCountArray: { value: number }[] = [];
  public pageDisplayLimitArray: { value: number }[] = [{ value: 5 }, { value: 10 }, { value: 20 }, { value: 30 }, { value: 50 }];

  constructor(
    private excelService: ExcelService,
    private bsModalService: BsModalService,
    private cacheService: CacheService,
    private objectPropertiesService: ObjectPropertiesService,
    private unitSharedService: UnitSharedService
  ) {
    this.unitSharedService.isUnitPortalStyle$.subscribe((isUnit) => {
      this.isUnitPortalStyle = isUnit;
    });
  }

  public ngOnInit(): void {
    this.setTableId();
    this.loadTableCache();
    this.setColumnWidth();
    this.reset?.subscribe(() => {
      this.currentPage = 1;
    });
  }

  private setTableId(): void {
    this.tableId = location.pathname + "/";

    this.columns.forEach((column) => {
      this.tableId += column.prop[0];
    });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (isObservable(this.data)) {
        this.data.subscribe((data: T[]) => {
          this.setData(data);
        });
      } else {
        this.setData(this.data);
      }

      this.setDisplayColumns();
    }, 100);
  }

  public setData(data: T[]): void {
    if (data) {
      this.originalData = data;
      this.filteredData = this.originalData;

      this.setMaxPage();
      this.setPageCountArray();
      this.setDisplayData();
    }
  }

  public searchDatatable(): void {
    this.filterTable();
    this.setMaxPage();
    this.setPageCountArray();
    this.setPage(1);
  }

  private filterTable(): void {
    const searchColsLength = this.searchCols.length;
    const keys = this.searchCols;
    const lowercaseFilterValue = this.filterValue ? this.filterValue.toLowerCase() : "";

    this.filteredData = this.originalData.filter((item) => {
      for (let i = 0; i < searchColsLength; i++) {
        const colValue = item[keys[i]];

        if (!lowercaseFilterValue || (!!colValue && colValue.toString().toLowerCase().indexOf(lowercaseFilterValue) !== -1)) {
          return true;
        }
      }
      return false;
    });

    this.setDisplayData();
  }

  public toggleAllSelected(): void {
    this.columns.forEach((column) => {
      column.selected = !this.allSelected;
    });
    this.setDisplayColumns();
  }

  public setDisplayColumns(): void {
    this.displayColumns = this.columns.filter((column) => column.selected);
    this.allSelected = this.columns.filter((column) => !column.selected).length === 0;
    this.saveTableCache();
  }

  private setColumnsFromModal(columns: Column[]): void {
    this.columns.forEach((column) => {
      const modalColumn = columns.find((modalColumn) => modalColumn.prop === column.prop);
      if (modalColumn) {
        column.selected = modalColumn.selected;
      }
    });
    this.setDisplayColumns();
  }

  private setColumnWidth(): void {
    if (!this.columnMode) {
      this.columnMode = "force";

      this.columns.forEach((column) => {
        if (column.hasOwnProperty("width")) this.columnMode = "fixed";
      });
    }
  }

  private loadTableCache(): void {
    const cachedColumns = this.cacheService.load(this.tableId);

    if (cachedColumns) {
      this.columns.forEach((column) => {
        const cachedColumn = cachedColumns.find((_cachedColumn) => _cachedColumn.prop === column.prop);
        if (cachedColumn) Object.assign(column, cachedColumn);
      });
    }
  }

  private saveTableCache(): void {
    const columnCache = this.columns.map((column) => ({
      name: column.name,
      prop: column.prop,
      selected: column.selected,
    }));

    this.cacheService.save({
      key: this.tableId,
      data: columnCache,
    });
  }

  public customSort(event: SortEvent): void {
    this.filteredData.sort((a, b) => {
      const sortDirection = event.sorts[0].dir;
      const prop = event.sorts[0].prop;
      if (sortDirection === "asc") {
        return a[prop] > b[prop] ? 1 : -1;
      } else {
        return a[prop] > b[prop] ? -1 : 1;
      }
    });
    this.setDisplayData();
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public exportExcel(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const exportData: any[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let tempData: { [key: string]: any } = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let dataValue: any;

    this.filteredData.forEach((data) => {
      tempData = {};
      this.displayColumns.forEach((column) => {
        if (column.name && column.selected) {
          dataValue = this.objectPropertiesService.getValue(data, column.prop);

          if (typeof dataValue === "boolean") tempData[column.name] = dataValue ? "Sí" : "No";
          else if (typeof dataValue === "string" && !isNaN(new Date(dataValue).getTime()))
            tempData[column.name] = DatePipe.transform(new Date(dataValue));
          else if (typeof data[column.prop] === "number") tempData[column.name] = NumberPipe.transform(dataValue);
          else tempData[column.name] = dataValue;
        }
      });

      exportData.push(tempData);
    });

    if (this.customExport) {
      this.excelExported.emit(exportData);
    } else {
      this.excelService.exportAsExcelFile(exportData, this.excelName || "");
    }
  }

  public activate(event: RowClickEvent<T>): void {
    if (this.clickableRow && event.type === "click") {
      this.onRowClick(event.row);
    }
  }

  private onRowClick(row: T): void {
    this.rowClicked.emit(row);
  }

  public showFilterModal(): void {
    this.filterTableModal = this.bsModalService.show(FilterTableModalServer, {
      initialState: {
        filterValue: this.filterValue,
        allSelected: this.allSelected,
        columns: this.columns,
      },
    });
    this.filterTableModal.content.onClose.subscribe((nuevoFiltro) => {
      this.filterValue = nuevoFiltro.filterValue;
      this.filterTable();
      this.setColumnsFromModal(nuevoFiltro.columns);
    });
  }

  public setMaxPage(): void {
    if (this.serverMode) {
      this.serverSidePagination?.subscribe((serverSidePagination) => {
        this.maxPage = serverSidePagination.maxPage;
        this.setPageCountArray();
      });
    } else {
      this.maxPage = Math.ceil(this.filteredData.length / this.pageSize);
    }
  }

  public setPageCountArray(): void {
    if (this.pageCountArray.length > this.maxPage) {
      this.pageCountArray = this.pageCountArray.slice(0, this.maxPage);
    } else {
      this.pageCountArray = [
        ...this.pageCountArray,
        ...Array.from({ length: this.maxPage - this.pageCountArray.length }, (_, i) => ({
          value: this.pageCountArray.length + i + 1,
        })),
      ];
    }
  }

  public setPageSize(newPageSize: number): void {
    this.pageSize = newPageSize;
    this.setMaxPage();
    this.setPageCountArray();

    if (this.currentPage > this.maxPage) this.currentPage = this.maxPage;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.table!.limit = this.pageSize;

    this.setDisplayData();
  }

  public setPage(newPage: number): void {
    if (this.serverMode) {
      // Emit an object that matches PaginationResponse
      const paginationResponse: PaginationResponse = {
        currentPage: newPage,
        pageSize: this.pageSize,
        maxPage: this.maxPage,
      };
      this.serviceConsuming.emit(paginationResponse);
      this.currentPage = newPage;
    } else {
      this.currentPage = newPage;
      this.offset = this.pageSize * (this.currentPage - 1);
      this.setDisplayData();
    }
  }

  private setDisplayData(): void {
    this.itemCount = this.filteredData.length;
    this.displayData = this.filteredData.slice(this.offset, this.offset + this.pageSize);
  }
}
